import { useEffect, useMemo, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { brand } from '@cloudgenie/tokens';
import { Logo } from './Logo';

export interface ProductShellProps {
  product: 'finops' | 'studio' | 'id';
  title:   string;
  subtitle?: string;
  children?: ReactNode;
  /**
   * Authenticated user session. When provided, ProductShell skips its
   * own `/api/auth/me` probe and immediately renders the user menu.
   * Callers wrapping the shell in <AuthGuard> should forward the guard's
   * user here so the header never briefly shows "Sign in →" after login.
   */
  user?: SessionUser;
}

/**
 * Session probe used by the shared header. Hits the same `/api/auth/me`
 * endpoint every app uses so we don't need to plumb auth context into
 * this shared component. Result cached in module state so multiple
 * shells on the same page don't re-fetch.
 */
type SessionUser = { email?: string; role?: string; name?: string } | null;
let _sessionCache: { user: SessionUser; ts: number } | null = null;

async function probeSession(): Promise<SessionUser> {
  if (_sessionCache && Date.now() - _sessionCache.ts < 30_000) return _sessionCache.user;
  try {
    const apiBase = (import.meta as any).env?.VITE_API_BASE || 'https://app.cloudgenie.co';
    const r = await fetch(`${apiBase}/api/auth/me`, { credentials: 'include' });
    if (!r.ok) { _sessionCache = { user: null, ts: Date.now() }; return null; }
    const data = await r.json();
    const user = data?.user || data || null;
    _sessionCache = { user, ts: Date.now() };
    return user;
  } catch {
    _sessionCache = { user: null, ts: Date.now() };
    return null;
  }
}

async function doLogout() {
  try {
    const apiBase = (import.meta as any).env?.VITE_API_BASE || 'https://app.cloudgenie.co';
    await fetch(`${apiBase}/api/auth/logout`, { method: 'POST', credentials: 'include' });
  } catch { /* best-effort */ }
  _sessionCache = { user: null, ts: Date.now() };
  window.location.href = 'https://id.cloudgenie.co/login';
}

const productLabel: Record<ProductShellProps['product'], string> = {
  finops: brand.productFinops,
  studio: brand.productStudio,
  id:     'CloudGenie',
};

const productBadge: Record<ProductShellProps['product'], string> = {
  finops: 'FINOPS',
  studio: 'STUDIO',
  id:     'IDENTITY',
};

const badgeAccent: Record<ProductShellProps['product'], string> = {
  finops: 'text-cyan-400 border-cyan-400/30 bg-cyan-400/5',
  studio: 'text-pink-400 border-pink-400/30 bg-pink-400/5',
  id:     'text-violet-400 border-violet-400/30 bg-violet-400/5',
};

/** Where in the app are we? Used to hide the redundant "Sign in →" header
 *  link when the user is already on an auth-flow page. */
function useOnAuthPage(): boolean {
  return useMemo(() => {
    if (typeof window === 'undefined') return false;
    const p = window.location.pathname.toLowerCase();
    return (
      p.startsWith('/login') ||
      p.startsWith('/signup') ||
      p.startsWith('/forgot-password') ||
      p.startsWith('/reset-password') ||
      p.startsWith('/authorize-cli')
    );
  }, []);
}

/**
 * Full-viewport dark shell used by every product app. Consistent nav, footer,
 * and login CTA across finops / studio / id — driven by shared tokens.
 *
 * When children are passed, they render as the main content and the hero
 * card disappears. When no children are passed, we render a rich landing
 * hero so the marketing sub-pages (finops.cloudgenie.co, studio.cloudgenie.co,
 * id.cloudgenie.co) don't look empty.
 */
export function ProductShell({ product, title, subtitle, children, user: userProp }: ProductShellProps) {
  const [user, setUser] = useState<SessionUser>(userProp ?? null);
  const [loaded, setLoaded] = useState(!!userProp);
  const onAuthPage = useOnAuthPage();

  useEffect(() => {
    if (userProp) {
      setUser(userProp);
      setLoaded(true);
      return;
    }
    probeSession().then((u) => { setUser(u); setLoaded(true); });
  }, [userProp]);

  const showSignIn = loaded && !user && !onAuthPage;
  const showUserMenu = loaded && user && user.email;

  return (
    <div className={clsx(
      'min-h-screen text-foreground flex flex-col relative bg-background',
    )} data-testid={`shell-${product}`}
      style={{
        // Layered radial gradients on top of the base bg — using explicit
        // hex values because Tailwind JIT cannot resolve `theme(...)` inside
        // arbitrary-value classes at runtime, which produced a broken
        // light-fallback background in the finops/studio shells.
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,102,241,0.18), transparent 60%),' +
          'radial-gradient(ellipse 60% 50% at 100% 10%, rgba(236,72,153,0.10), transparent 60%),' +
          'radial-gradient(ellipse 60% 40% at 0% 90%, rgba(34,211,238,0.08), transparent 60%)',
      }}
    >
      {/* Subtle grid texture behind the hero */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/70 border-b border-border/40">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 h-14">
          <a href="/" className="flex items-center gap-2 group" data-testid="shell-brand">
            <Logo product={product} size={26} />
            <span className="font-semibold tracking-tight">{productLabel[product]}</span>
            <span className={clsx(
              'hidden sm:inline-block ml-2 rounded-md border px-2 py-0.5 text-[10px] font-medium tracking-[0.15em]',
              badgeAccent[product],
            )}>
              {productBadge[product]}
            </span>
          </a>
          {!loaded ? (
            <span className="text-xs text-muted-foreground" data-testid="shell-loading">…</span>
          ) : showUserMenu ? (
            <div className="flex items-center gap-3 text-sm" data-testid="shell-user-menu">
              <span className="text-muted-foreground hidden sm:inline" data-testid="shell-user-email">{user!.email}</span>
              <button
                onClick={doLogout}
                className="text-muted-foreground hover:text-foreground transition"
                data-testid="shell-logout"
              >
                Sign out →
              </button>
            </div>
          ) : showSignIn ? (
            <a
              href={
                typeof window === 'undefined'
                  ? 'https://id.cloudgenie.co/login'
                  : `https://id.cloudgenie.co/login?next=${encodeURIComponent(window.location.href)}`
              }
              className="rounded-full border border-border bg-card/50 px-4 py-1.5 text-sm text-foreground hover:border-primary hover:bg-primary/10 transition"
              data-testid="shell-login"
            >
              Sign in →
            </a>
          ) : (
            /* auth page: skip the link, keep symmetry with an invisible spacer */
            <span aria-hidden className="w-16" />
          )}
        </div>
      </header>

      <main className="flex-1 relative z-10">
        {children ? (
          <div className="max-w-7xl mx-auto px-6 py-10">{children}</div>
        ) : (
          <HeroLanding product={product} title={title} subtitle={subtitle} />
        )}
      </main>

      <footer className="border-t border-border/40 relative z-10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <Logo product={product} size={16} />
            <p>© {new Date().getFullYear()} {brand.legalEntity}</p>
          </div>
          <div className="flex items-center gap-4">
            <a className="hover:text-foreground" href="https://www.cloudgenie.co">Home</a>
            <a className="hover:text-foreground" href="https://www.cloudgenie.co/legal/privacy">Privacy</a>
            <a className="hover:text-foreground" href="https://www.cloudgenie.co/legal/terms">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Hero landing card shown when the shell has no children — replaces the
 * previous "small centered subtitle" layout that users reported as dull.
 */
function HeroLanding({ product, title, subtitle }: { product: ProductShellProps['product']; title: string; subtitle?: string }) {
  const productFeatures: Record<ProductShellProps['product'], { label: string; icon: string; body: string }[]> = {
    finops: [
      { label: 'Cost attribution',  icon: '$',  body: 'Live AWS + Azure + GCP spend broken down by team, project, service, tag.' },
      { label: 'Anomaly detection', icon: '⚡',  body: 'Ship-day cost spikes surface within minutes, not month-end.' },
      { label: 'Savings AI',        icon: '◆',  body: 'Right-sizing + Reserved Instance ROI, driven by dependency-aware ML.' },
    ],
    studio: [
      { label: 'Schema-typed IaC',  icon: '{}',  body: 'Terraform, OpenTofu, ARM, CloudFormation, Ansible, Pulumi — one schema.' },
      { label: 'Preview + apply',   icon: '▶',  body: 'Plan/apply from the CLI, the shell, or your CI — audited every time.' },
      { label: 'Blueprints',        icon: '◇',  body: 'Ship reusable golden-path stacks in minutes, not weeks.' },
    ],
    id: [
      { label: 'One admin console',   icon: '◉',  body: 'Users, groups, secret brokers, KMS, SSO, workflows — one place.' },
      { label: 'Capability-based RBAC', icon: '◈',  body: '6 built-in roles + custom permissions + per-user overrides.' },
      { label: 'SOC 2 · ISO 27001',   icon: '✓',  body: 'Full control mapping ships with the platform.' },
    ],
  };

  const accentRing: Record<ProductShellProps['product'], string> = {
    finops: 'ring-cyan-400/40',
    studio: 'ring-pink-400/40',
    id:     'ring-violet-400/40',
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
      <div className="text-center max-w-3xl mx-auto">
        <span className={clsx(
          'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-medium tracking-[0.15em] uppercase',
          badgeAccent[product],
        )}>
          <span className={clsx('inline-block w-1.5 h-1.5 rounded-full ring-4', accentRing[product],
            product === 'finops' && 'bg-cyan-400',
            product === 'studio' && 'bg-pink-400',
            product === 'id'     && 'bg-violet-400',
          )} />
          {productBadge[product]} · CloudGenie
        </span>
        <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {subtitle}
          </p>
        )}
      </div>

      <div className="mt-16 grid gap-4 md:grid-cols-3">
        {productFeatures[product].map((f) => (
          <div key={f.label}
            className="group relative rounded-2xl border border-border/60 bg-card/40 backdrop-blur-sm p-6 hover:border-border transition"
          >
            <div className={clsx(
              'inline-flex items-center justify-center w-9 h-9 rounded-lg text-lg font-semibold',
              badgeAccent[product],
              'border',
            )}>
              {f.icon}
            </div>
            <div className="mt-4 font-semibold">{f.label}</div>
            <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{f.body}</div>
          </div>
        ))}
      </div>

      <div className="mt-14 text-center text-xs text-muted-foreground">
        Part of the CloudGenie multi-cloud FinOps platform · <a href="https://www.cloudgenie.co" className="hover:text-foreground underline underline-offset-4">Learn more</a>
      </div>
    </div>
  );
}
